import type { HelperAssignedItem } from '../../services/helper/helperAssignedItemsService'
import type { HelperMeetingItem } from '../../services/helper/helperMeetingService'
import {
  getEffectiveCompletion,
  isoDate,
  isoWeekKey,
  occurrenceKeyFor,
  startOfIsoWeek,
} from '../../services/recurringChecklistService'
import { getChecklistScheduleSpan } from '../../services/checklistDurationHelpers'
import { buildLockGraph, resolveLock } from '../../services/checklistLockService'
import type { RepeatPagesInput } from '../../services/repeat/types'

export type ViewMode = 'DAY' | 'THREE_DAY' | 'WEEK' | 'MONTH'

export type CalendarChecklistEntry = {
  key: string
  kind: 'INDIVIDUAL' | 'DAILY'
  item: HelperAssignedItem
  title: string
  date: Date
  occurrenceKey: string | null
  status: string
  completed: boolean
  isLocked?: boolean
  meta: string
}

export type WeeklyChecklistEntry = {
  key: string
  item: HelperAssignedItem
  occurrenceKey: string
  status: string
  completed: boolean
  isLocked?: boolean
  weekLabel: string
}

export const STATUS_DOT: Record<string, string> = {
  ASSIGNED: 'var(--info)',
  ACCEPTED: 'var(--chart-5)',
  ONGOING: 'var(--warning)',
  ASSESSING: 'var(--success)',
  COMPLETED: 'var(--success)',
  NOT_REQUIRED: 'var(--muted)',
  POSTPONED: 'var(--danger)',
  LOCKED: 'var(--danger)',
}

export const startOfDay = (date: Date): Date => {
  const next = new Date(date)
  next.setHours(0, 0, 0, 0)
  return next
}

export const addDays = (date: Date, count: number): Date => {
  const next = new Date(date)
  next.setDate(next.getDate() + count)
  return startOfDay(next)
}

const parseDateValue = (value?: string | null): Date | null => {
  if (!value) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number)
    return startOfDay(new Date(year, month - 1, day))
  }
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : startOfDay(parsed)
}

export const dayKey = (date: Date, timeZone?: string): string => isoDate(date, timeZone)

export const weekKey = (date: Date, timeZone?: string): string => isoWeekKey(date, timeZone)

const scheduleStart = (item: HelperAssignedItem): Date | null =>
  parseDateValue(getChecklistScheduleSpan(item)?.startDate)

const scheduleEnd = (item: HelperAssignedItem): Date | null =>
  parseDateValue(getChecklistScheduleSpan(item)?.endDate)

const isDateInsideItemWindow = (item: HelperAssignedItem, date: Date): boolean => {
  const time = startOfDay(date).getTime()
  const start = scheduleStart(item)?.getTime()
  const end = scheduleEnd(item)?.getTime()
  if (start !== undefined && time < start) return false
  if (end !== undefined && time > end) return false
  return true
}

const weekOverlapsItemWindow = (item: HelperAssignedItem, date: Date): boolean => {
  const weekStart = startOfIsoWeek(date)
  const weekEnd = addDays(weekStart, 6)
  const start = scheduleStart(item)
  const end = scheduleEnd(item)
  if (start && start.getTime() > weekEnd.getTime()) return false
  if (end && end.getTime() < weekStart.getTime()) return false
  return true
}

const meetingDate = (meeting: HelperMeetingItem): Date | null => {
  if (!meeting.startsAt) return null
  const parsed = new Date(meeting.startsAt)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export const formatMeetingTime = (meeting: HelperMeetingItem): string => {
  if (!meeting.startsAt) return 'Time TBD'
  const start = new Date(meeting.startsAt)
  const end = meeting.endsAt ? new Date(meeting.endsAt) : null
  const fmt = (date: Date) => date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  return end ? `${fmt(start)} - ${fmt(end)}` : fmt(start)
}

const formatDateRange = (start: Date, end: Date): string =>
  `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`

const normalizeStatus = (item: HelperAssignedItem, occurrenceKey: string | null): { status: string; completed: boolean } => {
  if (item.checklistType === 'DAILY' || item.checklistType === 'WEEKLY') {
    const effective = getEffectiveCompletion(item, occurrenceKey ?? undefined)
    const completed = effective.isCompleted
    return {
      completed,
      status: completed ? 'COMPLETED' : effective.workflowStatus ?? item.workflowStatus ?? item.effectiveStatus,
    }
  }
  return {
    completed: item.isCompleted === true || item.effectiveStatus === 'COMPLETED',
    status: item.effectiveStatus,
  }
}

export const statusLabel = (status: string): string => status.toLowerCase().replace(/_/g, ' ')

export const buildRangeDates = (view: ViewMode, anchor: Date): Date[] => {
  if (view === 'DAY') return [anchor]
  if (view === 'THREE_DAY') return [anchor, addDays(anchor, 1), addDays(anchor, 2)]
  if (view === 'WEEK') {
    const monday = startOfIsoWeek(anchor)
    return Array.from({ length: 7 }, (_, idx) => addDays(monday, idx))
  }
  return []
}

export const buildHelperCalendarModel = (
  items: readonly HelperAssignedItem[],
  meetings: readonly HelperMeetingItem[],
  dates: readonly Date[],
  timeZone?: string,
  pages?: RepeatPagesInput,
): {
  dayEntries: Map<string, CalendarChecklistEntry[]>
  meetingEntries: Map<string, HelperMeetingItem[]>
  weeklyEntries: WeeklyChecklistEntry[]
  dailyCount: number
  individualCount: number
  meetingCount: number
} => {
  const dayEntries = new Map<string, CalendarChecklistEntry[]>()
  const meetingEntries = new Map<string, HelperMeetingItem[]>()
  const weeklyMap = new Map<string, WeeklyChecklistEntry>()
  let dailyCount = 0
  let individualCount = 0
  let meetingCount = 0
  const visibleDayKeys = new Set(dates.map((date) => dayKey(date, timeZone)))
  const graph = buildLockGraph(items)

  const addEntry = (date: Date, entry: CalendarChecklistEntry) => {
    const key = dayKey(date, timeZone)
    const bucket = dayEntries.get(key) ?? []
    bucket.push(entry)
    dayEntries.set(key, bucket)
  }

  for (const meeting of meetings) {
    const date = meetingDate(meeting)
    if (!date) continue
    const key = dayKey(date, timeZone)
    if (!visibleDayKeys.has(key)) continue
    const bucket = meetingEntries.get(key) ?? []
    bucket.push(meeting)
    meetingEntries.set(key, bucket)
    meetingCount += 1
  }

  for (const item of items) {
    if (item.checklistType === 'DAILY') {
      for (const date of dates) {
        if (!isDateInsideItemWindow(item, date)) continue
        const occurrenceKey = occurrenceKeyFor('DAILY', date, timeZone)
        const effective = normalizeStatus(item, occurrenceKey)
        const lock = resolveLock(item, occurrenceKey, graph, pages, date, timeZone)
        dailyCount += 1
        addEntry(date, {
          key: `${item.id}:${occurrenceKey}`,
          kind: 'DAILY',
          item,
          title: item.title,
          date,
          occurrenceKey,
          status: effective.status,
          completed: effective.completed,
          isLocked: lock.isLocked,
          meta: 'Daily checklist',
        })
      }
      continue
    }

    if (item.checklistType === 'WEEKLY') {
      for (const date of dates) {
        if (!weekOverlapsItemWindow(item, date)) continue
        const weekStart = startOfIsoWeek(date, timeZone)
        const occurrenceKey = occurrenceKeyFor('WEEKLY', weekStart, timeZone)
        if (weeklyMap.has(`${item.id}:${occurrenceKey}`)) continue
        const effective = normalizeStatus(item, occurrenceKey)
        const lock = resolveLock(item, occurrenceKey, graph, pages, date, timeZone)
        weeklyMap.set(`${item.id}:${occurrenceKey}`, {
          key: `${item.id}:${occurrenceKey}`,
          item,
          occurrenceKey,
          status: effective.status,
          completed: effective.completed,
          isLocked: lock.isLocked,
          weekLabel: formatDateRange(weekStart, addDays(weekStart, 6)),
        })
      }
      continue
    }

    if (!getChecklistScheduleSpan(item)) continue
    const visibleDates = dates.filter((date) =>
      visibleDayKeys.has(dayKey(date, timeZone)) && isDateInsideItemWindow(item, date),
    )
    if (visibleDates.length === 0) continue
    const effective = normalizeStatus(item, null)
    individualCount += 1
    for (const date of visibleDates) {
      const dKey = dayKey(date, timeZone)
      const lock = resolveLock(item, dKey, graph, pages, date, timeZone)
      addEntry(date, {
        key: `${item.id}:${dKey}`,
        kind: 'INDIVIDUAL',
        item,
        title: item.title,
        date,
        occurrenceKey: null,
        status: effective.status,
        completed: effective.completed,
        isLocked: lock.isLocked,
        meta: 'Assigned checklist',
      })
    }
  }

  for (const bucket of dayEntries.values()) {
    bucket.sort((left, right) => {
      if (left.kind !== right.kind) return left.kind === 'DAILY' ? -1 : 1
      return left.title.localeCompare(right.title)
    })
  }

  for (const bucket of meetingEntries.values()) {
    bucket.sort((left, right) => (left.startsAt ?? 0) - (right.startsAt ?? 0))
  }

  return {
    dayEntries,
    meetingEntries,
    weeklyEntries: Array.from(weeklyMap.values()).sort((left, right) => left.item.title.localeCompare(right.item.title)),
    dailyCount,
    individualCount,
    meetingCount,
  }
}
